import { useIonRouter } from '@ionic/react';
import {
  Permission,
  type TeamMemberWithUserDto,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { Trash2 } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { AddTeamMemberDialog } from '@/core/components/workspace/teams/add-team-member-dialog';
import { DeleteTeamDialog } from '@/core/components/workspace/teams/delete-team-dialog';
import { RemoveTeamMemberDialog } from '@/core/components/workspace/teams/remove-team-member-dialog';
import { TeamInlineEditSection } from '@/core/components/workspace/teams/team-inline-edit-section';
import { TeamMembersCard } from '@/core/components/workspace/teams/team-members-card';
import {
  TEAM_DETAIL_MUTED_CLASS,
  TeamDetailSectionCard,
} from '@/core/components/workspace/teams/team-detail-section-card';
import { SettingsSection } from '@/core/components/settings/settings-section';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { TeamProfileTemplatesSection } from '@/core/components/workspace/teams/team-profile-templates-section';
import { useLocale } from '@/core/hooks/use-locale';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

function TeamDetailBreadcrumb({
  domainLabel,
  peopleLabel,
}: {
  domainLabel: string;
  peopleLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {peopleLabel}
    </>
  );
}

function useTeamDetailSplitLayout() {
  const [isSplitLayout, setIsSplitLayout] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : false,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsSplitLayout(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return isSplitLayout;
}

function TeamDetailPageContent({
  isSplitLayout,
  children,
}: {
  isSplitLayout: boolean;
  children: ReactNode;
}) {
  return (
    <AppPageContent
      scrollY={!isSplitLayout}
      className={isSplitLayout ? 'flex h-full min-h-0 flex-col' : undefined}
    >
      <AppPageLayout
        className={
          isSplitLayout
            ? 'min-h-0 flex-1 overflow-hidden pb-6'
            : 'min-w-0 overflow-x-hidden md:pb-6'
        }
      >
        {children}
      </AppPageLayout>
    </AppPageContent>
  );
}

export function TeamDetailPage() {
  const dict = useDictionary();
  const locale = useLocale();
  const isSplitLayout = useTeamDetailSplitLayout();
  const ionRouter = useIonRouter();
  const backRouter = useIonBackRouter();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);
  const redirect = (path: string) => {
    ionRouter.push(path, 'root', 'replace');
  };
  const { id: teamId } = useParams<{ id: string }>();
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const canManage = can(Permission.TEAMS_MANAGE);

  const [team, setTeam] = useState<TeamWithCountsDto | null>(null);
  const [members, setMembers] = useState<TeamMemberWithUserDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] =
    useState<TeamMemberWithUserDto | null>(null);

  const workspaceId = currentWorkspace?.id;
  const teamAssignmentDict = dict.workspace.profileTemplates.teamAssignment;

  const teamsTabPath = `${shellPaths.morePeople}?tab=teams`;

  const handleBack = () => {
    goBack(teamsTabPath);
  };

  const load = useCallback(async () => {
    if (!workspaceId || !teamId) return;
    setIsLoading(true);
    try {
      const [teamRes, membersRes] = await Promise.all([
        TeamsApi.findOne({ path: { workspaceId, teamId } }),
        TeamsApi.listMembers({ path: { workspaceId, teamId } }),
      ]);
      setTeam((teamRes.data ?? null) as TeamWithCountsDto | null);
      setMembers((membersRes.data ?? []) as TeamMemberWithUserDto[]);
    } catch (err) {
      console.error('Failed to load team:', err);
      toast.error(dict.workspace.teams.errors.notFound);
      ionRouter.push(teamsTabPath, 'root', 'replace');
    } finally {
      setIsLoading(false);
    }
  }, [
    dict.workspace.teams.errors.notFound,
    ionRouter,
    teamId,
    teamsTabPath,
    workspaceId,
  ]);

  if (workspaceId && !teamId && isLoading) {
    setIsLoading(false);
  }

  useEffect(() => {
    if (!workspaceId || !teamId) return;
    void (async () => {
      await load();
    })();
  }, [load, teamId, workspaceId]);

  const membersDict = {
    membersTitle: dict.workspace.teams.detail.membersTitle,
    noMembers: dict.workspace.teams.detail.noMembers,
    addMember: dict.workspace.teams.actions.addMember,
  };

  const breadcrumb = (
    <TeamDetailBreadcrumb
      domainLabel={dict.nav.navDomainPeople}
      peopleLabel={dict.nav.people}
    />
  );

  const generalSection = isLoading ? (
    <TeamDetailSectionCard>
      <Skeleton className="h-48 w-full rounded-[var(--r-control)]" />
    </TeamDetailSectionCard>
  ) : canManage && team && workspaceId ? (
    <TeamInlineEditSection
      workspaceId={workspaceId}
      team={team}
      onUpdated={setTeam}
      dict={{
        generalTitle: dict.workspace.teams.detail.generalTitle,
        name: dict.workspace.teams.form.name,
        namePlaceholder: dict.workspace.teams.form.namePlaceholder,
        description: dict.workspace.teams.form.description,
        descriptionPlaceholder:
          dict.workspace.teams.form.descriptionPlaceholder,
        color: dict.workspace.teams.form.color,
        saving: dict.workspace.teams.form.saving,
        saveError: dict.workspace.teams.editDialog.error,
        errorNameTaken: dict.workspace.teams.createDialog.errorNameTaken,
      }}
    />
  ) : team ? (
    <SettingsSection id="team-general-readonly" title={team.name} grouped>
      <SettingsGroupRows>
        <SettingsGroupRow>
          {team.description ? (
            <p className={TEAM_DETAIL_MUTED_CLASS}>{team.description}</p>
          ) : (
            <p className={TEAM_DETAIL_MUTED_CLASS}>—</p>
          )}
        </SettingsGroupRow>
      </SettingsGroupRows>
    </SettingsSection>
  ) : null;

  const templateSection =
    workspaceId && team && (canManage || !isLoading) ? (
      <TeamProfileTemplatesSection
        workspaceId={workspaceId}
        teamId={team.id}
        canManage={canManage}
        locale={locale}
        dict={teamAssignmentDict}
      />
    ) : isLoading ? (
      <TeamDetailSectionCard>
        <Skeleton className="h-36 w-full rounded-[var(--r-control)]" />
      </TeamDetailSectionCard>
    ) : null;

  const sidebarContent = (
    <div className="flex w-full min-w-0 flex-col gap-4 md:gap-5">
      {generalSection}
      {templateSection}
    </div>
  );

  if (!currentWorkspace) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader
              title={dict.workspace.teams.title}
              breadcrumb={breadcrumb}
              description={dict.workspace.teams.selectWorkspace}
            />
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  const layoutGridClass =
    'grid w-full min-w-0 grid-cols-1 items-start gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(260px,20rem)] lg:gap-[26px] xl:grid-cols-[minmax(0,1fr)_minmax(280px,22rem)] 2xl:grid-cols-[minmax(0,1fr)_minmax(300px,24rem)]';

  return (
    <AppIonPage>
      <TeamDetailPageContent isSplitLayout={isSplitLayout}>
        <PageHeader
          title={team?.name ?? dict.workspace.teams.title}
          breadcrumb={breadcrumb}
          backHref={teamsTabPath}
          onBack={handleBack}
          actions={
            canManage && team ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
                aria-label={dict.workspace.teams.actions.delete}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : undefined
          }
        />
        <div
          className={
            isSplitLayout
              ? `${layoutGridClass} h-full min-h-0 overflow-hidden`
              : 'flex w-full min-w-0 flex-col gap-4 md:gap-5'
          }
        >
          <div
            className={
              isSplitLayout
                ? 'flex h-full min-h-0 min-w-0 flex-col items-start'
                : 'flex min-w-0 flex-col gap-4 md:gap-5'
            }
          >
            {!isSplitLayout ? sidebarContent : null}

            <TeamMembersCard
              members={members}
              memberCount={team?.memberCount}
              isLoading={isLoading}
              canManage={canManage}
              scrollable={isSplitLayout}
              onAddMember={() => setAddMemberOpen(true)}
              onRemoveMember={setMemberToRemove}
              dict={membersDict}
            />
          </div>

          {isSplitLayout ? (
            <aside className="hidden min-h-0 w-full min-w-0 shrink-0 flex-col gap-4 md:gap-5 lg:flex">
              {sidebarContent}
            </aside>
          ) : null}
        </div>

        {team ? (
          <>
            <DeleteTeamDialog
              open={deleteOpen}
              onOpenChange={setDeleteOpen}
              workspaceId={currentWorkspace.id}
              teamId={team.id}
              teamName={team.name}
              onSuccess={() => redirect(teamsTabPath)}
              dict={{
                title: dict.workspace.teams.deleteDialog.title,
                description: dict.workspace.teams.deleteDialog.description,
                confirm: dict.workspace.teams.deleteDialog.confirm,
                deleting: dict.workspace.teams.deleteDialog.deleting,
                cancel: dict.workspace.teams.form.cancel,
                success: dict.workspace.teams.deleteDialog.success,
                error: dict.workspace.teams.deleteDialog.error,
              }}
            />

            <AddTeamMemberDialog
              open={addMemberOpen}
              onOpenChange={setAddMemberOpen}
              workspaceId={currentWorkspace.id}
              teamId={team.id}
              currentTeamMembers={members}
              onSuccess={() => void load()}
              dict={{
                title: dict.workspace.teams.addMemberDialog.title,
                description: dict.workspace.teams.addMemberDialog.description,
                memberLabel: dict.workspace.teams.addMemberDialog.memberLabel,
                searchPlaceholder:
                  dict.workspace.teams.addMemberDialog.searchPlaceholder,
                noAvailable: dict.workspace.teams.addMemberDialog.noAvailable,
                noResults: dict.workspace.teams.addMemberDialog.noResults,
                selectedCount:
                  dict.workspace.teams.addMemberDialog.selectedCount,
                add: dict.workspace.teams.addMemberDialog.add,
                addWithCount: dict.workspace.teams.addMemberDialog.addWithCount,
                adding: dict.workspace.teams.addMemberDialog.adding,
                cancel: dict.workspace.teams.form.cancel,
                success: dict.workspace.teams.addMemberDialog.success,
                successPlural:
                  dict.workspace.teams.addMemberDialog.successPlural,
                error: dict.workspace.teams.addMemberDialog.error,
                errorPartial: dict.workspace.teams.addMemberDialog.errorPartial,
                errorAlreadyInTeam:
                  dict.workspace.teams.addMemberDialog.errorAlreadyInTeam,
              }}
            />

            {memberToRemove ? (
              <RemoveTeamMemberDialog
                open={!!memberToRemove}
                onOpenChange={(o) => !o && setMemberToRemove(null)}
                workspaceId={currentWorkspace.id}
                teamId={team.id}
                teamName={team.name}
                teamMemberId={memberToRemove.id}
                memberName={(() => {
                  const u = memberToRemove.user;
                  if (u?.firstName && u?.lastName)
                    return `${u.firstName} ${u.lastName}`;
                  if (u?.firstName) return u.firstName;
                  if (u?.email) return u.email;
                  return memberToRemove.userId;
                })()}
                onSuccess={() => void load()}
                dict={{
                  title: dict.workspace.teams.removeMemberDialog.title,
                  description:
                    dict.workspace.teams.removeMemberDialog.description,
                  confirm: dict.workspace.teams.removeMemberDialog.confirm,
                  removing: dict.workspace.teams.removeMemberDialog.removing,
                  cancel: dict.workspace.teams.form.cancel,
                  success: dict.workspace.teams.removeMemberDialog.success,
                  error: dict.workspace.teams.removeMemberDialog.error,
                }}
              />
            ) : null}
          </>
        ) : null}
      </TeamDetailPageContent>
    </AppIonPage>
  );
}
